import React from "react";
import { ArrowLeft, Clock, User } from "lucide-react";
import { Button } from "./ui/button";

const BlogPost1 = ({ onBack }) => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Button 
        variant="ghost" 
        onClick={onBack}
        className="mb-8 text-credentra-bright-teal hover:text-credentra-dark-teal"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Blog
      </Button>
      
      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-credentra-dark-teal mb-4">
            The FICA Tip Credit, Explained in 3 Minutes
          </h1>
          <div className="flex items-center gap-4 text-gray-600 text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              3 min read
            </div>
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Credentra Team
            </div>
          </div>
        </header>

        <div className="space-y-6 text-gray-700 leading-relaxed">
          <p className="text-xl text-gray-600 font-medium">
            If you run a restaurant where customers tip your employees, you might be leaving thousands of dollars on the table every year. Here's what every restaurant owner and CPA needs to know about the FICA tip credit.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">What is the FICA Tip Credit?</h2>
          <p>
            The FICA tip credit is a dollar-for-dollar tax credit that restaurants can claim for the employer portion of Social Security and Medicare taxes paid on employee tips. Think of it as the IRS saying, "We know you're already paying extra taxes on tips, so here's some money back."
          </p>

          <p>
            Here's the simple math: When your server receives a $20 tip, you as the employer still have to pay 7.65% in FICA taxes on that tip (that's $1.53). The tip credit lets you get that $1.53 back as a direct reduction in your tax bill.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Who Can Claim It?</h2>
          <p>
            Any restaurant, bar, or food service business where employees receive tips can potentially claim this credit. The key requirements are:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Your employees receive tips from customers</li>
            <li>You pay employer FICA taxes on those tips</li>
            <li>You keep proper records of tip reporting</li>
          </ul>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">Why Do So Many Restaurants Miss This?</h2>
          <p>
            Honestly? It's complicated and easy to overlook. Most restaurant owners are focused on running their business, not diving deep into tax code. Plus, the calculation requires:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Matching tip data from your POS system</li>
            <li>Cross-referencing payroll records</li>
            <li>Calculating the exact employer FICA portion</li>
            <li>Filing the right forms with the IRS</li>
          </ul>

          <p>
            Many CPAs know about the credit but don't have the time to dig through months of POS and payroll data to calculate it manually. That's where automation comes in.
          </p>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">How Much Could You Save?</h2>
          <p>
            The amount varies based on your tip volume, but here are some rough estimates:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Small restaurant ($50K annual tips): ~$3,800 potential credit</li>
            <li>Medium restaurant ($150K annual tips): ~$11,500 potential credit</li>
            <li>Large restaurant ($300K annual tips): ~$23,000 potential credit</li>
          </ul>

          <div className="bg-credentra-bright-teal/10 border-l-4 border-credentra-bright-teal p-6 my-8">
            <p className="font-semibold text-credentra-dark-teal mb-2">Important Note:</p>
            <p className="text-sm">
              These are hypothetical examples for illustration only. Actual credits depend on your specific business circumstances, employee tip reporting, and other factors. Always consult with a qualified tax professional regarding your specific situation.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-credentra-dark-teal mt-8 mb-4">The Bottom Line</h2>
          <p>
            The FICA tip credit isn't just "nice to have" money—for many restaurants, it's a meaningful amount that could help with equipment upgrades, staff bonuses, or just improving cash flow. If you've been in business for a while and haven't claimed this credit, you might even be able to file amended returns for previous years.
          </p>

          <p>
            The key is having clean, organized data and knowing how to navigate the IRS requirements. That's exactly what modern automation tools are designed to handle, so you can focus on what you do best: running your restaurant.
          </p>
        </div>
      </article>
    </div>
  );
};

export default BlogPost1;